// src/components/appSettings/Banner/BannerForm/bannerFormHooks.js
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  createBannerThunk,
  updateBannerThunk,
} from "../../../../store/thunks/appSettingsThunks";

// Default form values
const DEFAULT_BANNER_FORM = {
  title: "",
  subtitle: "",
  internalName: "",
  action: {
    type: "NAVIGATION",
    screen: "",
    params: {},
    url: "",
    deeplink: "",
  },
  targeting: {
    branches: [],
    regions: [],
    customerGroups: [],
    deviceTypes: ["ALL"],
    minAppVersion: "",
    maxAppVersion: "",
  },
  displayRules: {
    priority: 0,
    position: 1,
    showOnlyOnce: false,
    maxImpressions: null,
    daysOfWeek: [],
    timeSlots: [],
    excludeDates: [],
  },
  validFrom: new Date().toISOString().split("T")[0],
  validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split("T")[0],
  isActive: true,
  tags: [],
  campaign: "",
  notes: "",
};

export const useBannerForm = (initialData = null, mode = "add") => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState(() => {
    if (mode === "edit" && initialData) {
      return parseBannerData(initialData);
    }
    return DEFAULT_BANNER_FORM;
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Image state
  const [imageFiles, setImageFiles] = useState({
    desktop: null,
    mobile: null,
    tablet: null,
  });

  const [imagePreviews, setImagePreviews] = useState({
    desktop: null,
    mobile: null,
    tablet: null,
  });

  // Update form when initial data changes
  useEffect(() => {
    if (mode === "edit" && initialData) {
      const parsed = parseBannerData(initialData);
      setFormData(parsed);

      // Set image previews from existing images
      if (initialData.images) {
        setImagePreviews({
          desktop: initialData.images.desktop || null,
          mobile: initialData.images.mobile || null,
          tablet: initialData.images.tablet || null,
        });
      }
    }
  }, [initialData, mode]);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle nested fields
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === "checkbox" ? checked : value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle image upload
  const handleImageUpload = (type, file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should not exceed 5MB");
      return;
    }

    setImageFiles((prev) => ({
      ...prev,
      [type]: file,
    }));

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreviews((prev) => ({
        ...prev,
        [type]: reader.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  // Handle image remove
  const handleImageRemove = (type) => {
    setImageFiles((prev) => ({
      ...prev,
      [type]: null,
    }));
    setImagePreviews((prev) => ({
      ...prev,
      [type]: null,
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Basic validations
    if (!formData.title || formData.title.trim().length < 3) {
      newErrors.title = "Title is required (min 3 characters)";
    }

    // Image validations
    if (mode === "add") {
      if (!imageFiles.desktop) {
        newErrors.desktopImage = "Desktop image is required";
      }
      if (!imageFiles.mobile) {
        newErrors.mobileImage = "Mobile image is required";
      }
    }

    // Date validations
    if (new Date(formData.validFrom) > new Date(formData.validUntil)) {
      newErrors.validUntil = "End date must be after start date";
    }

    // Action validations
    if (formData.action.type === "NAVIGATION" && !formData.action.screen) {
      newErrors["action.screen"] = "Screen is required for navigation action";
    }
    if (
      (formData.action.type === "WEBVIEW" ||
        formData.action.type === "EXTERNAL") &&
      !formData.action.url
    ) {
      newErrors["action.url"] = "URL is required";
    }
    if (formData.action.type === "DEEPLINK" && !formData.action.deeplink) {
      newErrors["action.deeplink"] = "Deeplink is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      const submitData = new FormData();

      console.log("Submitting form data:", { formData, mode });

      // ✅ CREATE A SINGLE JSON OBJECT WITH ALL DATA
      const jsonData = {
        title: formData.title,
        subtitle: formData.subtitle || "",
        internalName: formData.internalName || "",
        action: {
          type: formData.action.type,
          screen: formData.action.screen || "",
          url: formData.action.url || "",
          deeplink: formData.action.deeplink || "",
          params: formData.action.params || {},
        },
        targeting: {
          branches: formData.targeting.branches || [],
          regions: formData.targeting.regions || [],
          customerGroups: formData.targeting.customerGroups || [],
          deviceTypes: formData.targeting.deviceTypes || ["ALL"],
          minAppVersion: formData.targeting.minAppVersion || "",
          maxAppVersion: formData.targeting.maxAppVersion || "",
        },
        displayRules: {
          priority: formData.displayRules.priority || 0,
          position: formData.displayRules.position || 1,
          showOnlyOnce: formData.displayRules.showOnlyOnce || false,
          maxImpressions: formData.displayRules.maxImpressions || null,
          daysOfWeek: formData.displayRules.daysOfWeek || [],
          timeSlots: formData.displayRules.timeSlots || [],
          excludeDates: formData.displayRules.excludeDates || [],
        },
        validFrom: formData.validFrom,
        validUntil: formData.validUntil,
        isActive: formData.isActive,
        tags: formData.tags || [],
        campaign: formData.campaign || "",
        notes: formData.notes || "",
      };

      // ✅ APPEND AS SINGLE 'data' FIELD
      submitData.append("data", JSON.stringify(jsonData));

      console.log("JSON Data being sent:", jsonData); // Debug log

      // ✅ APPEND IMAGES
      if (imageFiles.desktop) {
        submitData.append("desktopImage", imageFiles.desktop);
      }
      if (imageFiles.mobile) {
        submitData.append("mobileImage", imageFiles.mobile);
      }
      if (imageFiles.tablet) {
        submitData.append("tabletImage", imageFiles.tablet);
      }

      // Submit
      if (mode === "add") {
        await dispatch(createBannerThunk(submitData)).unwrap();
        toast.success("Banner created successfully");
      } else {
        await dispatch(
          updateBannerThunk({
            id: initialData._id,
            bannerData: submitData,
          })
        ).unwrap();
        toast.success("Banner updated successfully");
      }

      navigate("/app-settings/banners");
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error.message || `Failed to ${mode} banner`);
    } finally {
      setSubmitting(false);
    }
  };

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    submitting,
    imageFiles,
    imagePreviews,
    handleChange,
    handleImageUpload,
    handleImageRemove,
    handleSubmit,
    validateForm,
  };
};

// Helper function to parse banner data for edit mode
function parseBannerData(banner) {
  return {
    title: banner.title || "",
    subtitle: banner.subtitle || "",
    internalName: banner.internalName || "",
    action: {
      type: banner.action?.type || "NAVIGATION",
      screen: banner.action?.screen || "",
      params: banner.action?.params || {},
      url: banner.action?.url || "",
      deeplink: banner.action?.deeplink || "",
    },
    targeting: {
      branches: banner.targeting?.branches || [],
      regions: banner.targeting?.regions || [],
      customerGroups: banner.targeting?.customerGroups || [],
      deviceTypes: banner.targeting?.deviceTypes || ["ALL"],
      minAppVersion: banner.targeting?.minAppVersion || "",
      maxAppVersion: banner.targeting?.maxAppVersion || "",
    },
    displayRules: {
      priority: banner.displayRules?.priority || 0,
      position: banner.displayRules?.position || 1,
      showOnlyOnce: banner.displayRules?.showOnlyOnce || false,
      maxImpressions: banner.displayRules?.maxImpressions || null,
      daysOfWeek: banner.displayRules?.daysOfWeek || [],
      timeSlots: banner.displayRules?.timeSlots || [],
      excludeDates: banner.displayRules?.excludeDates || [],
    },
    validFrom: banner.validFrom
      ? new Date(banner.validFrom).toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0],
    validUntil: banner.validUntil
      ? new Date(banner.validUntil).toISOString().split("T")[0]
      : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
    isActive: banner.isActive !== undefined ? banner.isActive : true,
    tags: banner.tags || [],
    campaign: banner.campaign || "",
    notes: banner.notes || "",
  };
}
